import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { youtubeThumbnailUrl } from "~/lib/youtubeUtils";
import YoutubeIcon from "../icons/YoutubeIcon";

const YOUTUBE_ORIGIN = "https://www.youtube.com";
const YT_STATE_PLAYING = 1;
const YT_PLACEHOLDER_WIDTH = 120;
const START_TIMEOUT_MS = 1500;

// Touch: player loads when shown; an overlay turns taps into play/pause so swipes reach the carousel
// (WOV-2253). If the phone blocks the first play, the overlay steps aside for YouTube's play button.
const WvYoutubeCarouselPlayer: FC<{
  videoId: string;
  title: string;
  active: boolean;
}> = ({ videoId, title, active }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isPlaying = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const [tapThrough, setTapThrough] = useState(false);
  const [posterSrc, setPosterSrc] = useState(
    youtubeThumbnailUrl(videoId, "maxresdefault")
  );
  const fallbackPoster = () =>
    setPosterSrc(youtubeThumbnailUrl(videoId, "hqdefault"));

  const command = useCallback((func: "playVideo" | "pauseVideo") => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      YOUTUBE_ORIGIN
    );
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const onMessage = (event: MessageEvent) => {
      if (
        event.origin !== YOUTUBE_ORIGIN ||
        event.source !== iframeRef.current?.contentWindow ||
        typeof event.data !== "string"
      ) {
        return;
      }
      let state: unknown;
      try {
        const data = JSON.parse(event.data);
        state =
          data.event === "onStateChange"
            ? data.info
            : data.event === "infoDelivery"
            ? data.info?.playerState
            : undefined;
      } catch {
        return;
      }
      if (typeof state !== "number") return;
      isPlaying.current = state === YT_STATE_PLAYING;
      if (isPlaying.current) setHasPlayed(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [loaded]);

  useEffect(() => {
    if (active && window.matchMedia("(pointer: coarse)").matches) {
      setLoaded(true);
    }
  }, [active]);

  // Don't keep playing once swiped away.
  useEffect(() => {
    if (!active && isPlaying.current) command("pauseVideo");
  }, [active, command]);

  if (!loaded) {
    return (
      <button
        type="button"
        className="product-carousel__video-poster"
        aria-label={title}
        onClick={(event) => {
          // Start the video instead of opening the full-screen preview.
          event.stopPropagation();
          setAutoplay(true);
          setLoaded(true);
        }}
      >
        <img
          className="product-viewport__img object-cover"
          src={posterSrc}
          alt=""
          loading="lazy"
          onLoad={(event) => {
            if (event.currentTarget.naturalWidth <= YT_PLACEHOLDER_WIDTH) {
              fallbackPoster();
            }
          }}
          onError={fallbackPoster}
        />
        <YoutubeIcon className="product-carousel__video-icon position-absolute" />
      </button>
    );
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        className="product-carousel__video"
        src={`${YOUTUBE_ORIGIN}/embed/${videoId}?autoplay=${
          autoplay ? 1 : 0
        }&enablejsapi=1&playsinline=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        // Subscribes to player state events.
        onLoad={() =>
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: "listening", id: videoId }),
            YOUTUBE_ORIGIN
          )
        }
      />
      {(hasPlayed || !tapThrough) && (
        <div
          className="product-carousel__video-tap"
          onClick={(event) => {
            event.stopPropagation();
            if (isPlaying.current) {
              command("pauseVideo");
              return;
            }
            command("playVideo");
            if (!hasPlayed) {
              window.setTimeout(() => {
                if (!isPlaying.current) setTapThrough(true);
              }, START_TIMEOUT_MS);
            }
          }}
        />
      )}
    </>
  );
};

export default WvYoutubeCarouselPlayer;
